'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { Icon } from '../shared/Icon';
import { Emphasised, SkImage, SkLink, text, type Localized, type SkImageRef } from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';

/**
 * Home hero: a full-bleed image with the headline set on a quiet panel.
 *
 * <p>The headline sits on a solid white panel rather than directly on the
 * photograph, so its contrast never depends on the picture an operator
 * uploads. On small screens the panel drops below the image instead of
 * covering it.
 *
 * <p>The CTA is a text link with an arrow, not a button: the brief's hero is
 * calm, and the planner is an invitation rather than a checkout.
 */
export interface SpatialKitchenHeroProps {
  image?: SkImageRef;
  eyebrow?: Localized;
  title?: Localized;
  /** One phrase inside `title` to set in bold. */
  emphasis?: Localized;
  intro?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  /**
   * A second, lower-commitment way out of the hero.
   *
   * <p>A hero carrying one high-commitment call to action converts only the
   * visitor who had already decided before they arrived. The designer is the
   * right primary, and this is for the larger group who are not ready to plan a
   * room and would otherwise leave.
   */
  secondaryCtaLabel?: Localized;
  secondaryCtaHref?: string;
  /**
   * A short line of fact under the intro — a price anchor, a lead time. Kept
   * out of `intro` so it can be styled as data rather than prose, and so an
   * operator can change a price without rewriting the sentence around it.
   */
  meta?: Localized;
  /** Which side the text panel sits on at desktop widths. */
  panelPosition?: 'left' | 'right';
}

const CSS = `
.sk-hero{position:relative;background:var(--sk-warm)}
.sk-hero__media{position:relative;height:clamp(200px,48vw,360px)}
.sk-hero__panel-wrap{position:relative}
.sk-hero__panel{background:var(--sk-white);padding:2rem var(--sk-gutter) 2.5rem;display:grid;gap:1.25rem;border-top:4px solid var(--sk-sand)}
.sk-hero__title{font-size:clamp(2.25rem,1.5rem + 3vw,4.25rem)}
.sk-hero__meta{margin:-.5rem 0 0;font-size:.9375rem;color:var(--sk-muted)}
.sk-hero__meta strong{color:var(--sk-ink);font-weight:600}
/* Wraps to one per line on a narrow phone rather than letting the two links
   collide; the gap is large enough that they never read as one control. */
.sk-hero__ctas{display:flex;flex-wrap:wrap;align-items:center;gap:.75rem 1.75rem;margin:0}
@media (min-width:769px){
  .sk-hero__media{height:clamp(480px,65vh,680px)}
  .sk-hero__panel-wrap{position:absolute;inset:auto 0 clamp(2.5rem,5vw,4.5rem) 0}
  .sk-hero__panel{max-width:min(40rem,calc(100% - 2 * var(--sk-gutter)));padding:clamp(2rem,3vw,3rem);border-radius:4px;box-shadow:0 20px 48px rgba(32,38,34,.16)}
  .sk-hero--right .sk-hero__panel{margin-left:auto}
}
`;

export function SpatialKitchenHero(props: SpatialKitchenHeroProps) {
  const title = text(props.title, 'Kitchens made to measure, planned around the way you live');
  const emphasis = text(props.emphasis);
  const eyebrow = text(props.eyebrow);
  const intro = text(props.intro);
  const ctaLabel = text(props.ctaLabel, 'Open Kitchen Designer');
  const ctaHref = props.ctaHref || '/kitchens/planner/';
  const secondaryCtaLabel = text(props.secondaryCtaLabel);
  const secondaryCtaHref = props.secondaryCtaHref;
  const meta = text(props.meta);
  const right = props.panelPosition === 'right';
  const titleId = useId();

  return (
    <BlockRoot as="section" className={`sk sk-hero${right ? ' sk-hero--right' : ''}`} aria-labelledby={titleId}>
      <SkBaseStyles />
      <SkStyles id="hero" css={CSS} />
      <div className="sk-hero__media sk-media">
        <SkImage image={props.image} priority sizes="100vw" />
      </div>
      <div className="sk-hero__panel-wrap">
        <div className="sk-container">
          <div className="sk-hero__panel">
            {eyebrow && <p className="sk-eyebrow">{eyebrow}</p>}
            <h1 id={titleId} className="sk-heading sk-hero__title">
              <Emphasised title={title} emphasis={emphasis} />
            </h1>
            {intro && <p className="sk-lead">{intro}</p>}
            {meta && <p className="sk-hero__meta">{meta}</p>}
            <p className="sk-hero__ctas">
              <SkLink href={ctaHref} className="sk-btn sk-btn--primary">
                {ctaLabel}
                <Icon name="arrow-right" size={18} />
              </SkLink>
              {secondaryCtaLabel && (
                <SkLink href={secondaryCtaHref || '/categories'} className="sk-btn sk-btn--outline">
                  {secondaryCtaLabel}
                  <Icon name="arrow-right" size={18} />
                </SkLink>
              )}
            </p>
          </div>
        </div>
      </div>
    </BlockRoot>
  );
}
